import { isPlatformBrowser, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import type { HeroGalleryItemDto } from '../../shared/models/marketing.models';
import { createH1SliderSwipeHandlers, sliderTrackTransform } from '../utils/h1-slider-swipe';

@Component({
  selector: 'app-photo-gallery-lightbox',
  standalone: true,
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (open()) {
      <div
        class="cg-photo-gallery"
        [class.cg-photo-gallery--closing]="closing()"
        role="dialog"
        aria-modal="true"
        aria-label="Photo gallery"
        (click)="onBackdropClick($event)"
      >
        <button type="button" class="cg-photo-gallery__backdrop" aria-label="Close gallery" (click)="close()"></button>

        <div class="cg-photo-gallery__shell">
          <button type="button" class="cg-photo-gallery__close" aria-label="Close" (click)="close()">
            <span aria-hidden="true">&times;</span>
          </button>

          @if (items.length > 1) {
            <button
              type="button"
              class="cg-photo-gallery__nav cg-photo-gallery__nav--prev"
              aria-label="Previous image"
              [disabled]="activeIndex() === 0"
              (click)="goPrev()"
            >
              <span aria-hidden="true">&#8249;</span>
            </button>
            <button
              type="button"
              class="cg-photo-gallery__nav cg-photo-gallery__nav--next"
              aria-label="Next image"
              [disabled]="activeIndex() === items.length - 1"
              (click)="goNext()"
            >
              <span aria-hidden="true">&#8250;</span>
            </button>
          }

          <div
            #viewport
            class="cg-photo-gallery__viewport"
            (pointerdown)="swipe.onPointerDown($event)"
            (pointermove)="swipe.onPointerMove($event)"
            (pointerup)="swipe.onPointerUp($event)"
            (pointercancel)="swipe.onPointerCancel($event)"
          >
            <div
              class="cg-photo-gallery__track"
              [class.cg-photo-gallery__track--dragging]="isDragging()"
              [style.transform]="trackTransform()"
            >
              @for (item of items; track item.image.cdnUrl; let i = $index) {
                <figure class="cg-photo-gallery__slide">
                  <img
                    [src]="shouldLoadImage(i) ? item.image.cdnUrl : null"
                    [alt]="item.image.alt ?? item.caption ?? ''"
                    [attr.loading]="i === activeIndex() ? 'eager' : 'lazy'"
                    decoding="async"
                    draggable="false"
                  />
                  @if (item.caption) {
                    <figcaption class="cg-photo-gallery__caption">{{ item.caption }}</figcaption>
                  }
                </figure>
              }
            </div>
          </div>

          @if (items.length > 1) {
            <div class="cg-photo-gallery__meta">
              <span class="cg-photo-gallery__counter">{{ activeIndex() + 1 }} / {{ items.length }}</span>
            </div>
            <div class="cg-photo-gallery__thumbs" role="tablist" aria-label="Gallery thumbnails">
              @for (item of items; track item.image.cdnUrl; let i = $index) {
                <button
                  type="button"
                  class="cg-photo-gallery__thumb"
                  role="tab"
                  [attr.aria-selected]="i === activeIndex()"
                  [attr.aria-label]="'Image ' + (i + 1)"
                  [ngClass]="{ 'cg-photo-gallery__thumb--active': i === activeIndex() }"
                  (click)="goTo(i)"
                >
                  <img
                    [src]="item.image.cdnUrl"
                    alt=""
                    loading="lazy"
                    decoding="async"
                    draggable="false"
                  />
                </button>
              }
            </div>
          }
        </div>
      </div>
    }
  `,
})
export class PhotoGalleryLightboxComponent implements OnChanges, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private closeTimer?: ReturnType<typeof setTimeout>;
  private previousBodyOverflow = '';

  @ViewChild('viewport') viewportRef?: ElementRef<HTMLElement>;

  @Input({ required: true }) items: HeroGalleryItemDto[] = [];
  @Input() startIndex = 0;

  readonly open = signal(false);
  readonly closing = signal(false);
  readonly activeIndex = signal(0);
  readonly dragOffsetPx = signal(0);
  readonly isDragging = signal(false);

  readonly swipe = createH1SliderSwipeHandlers({
    activeSlide: this.activeIndex,
    slideCount: () => this.items.length,
    viewportWidth: () => this.viewportRef?.nativeElement?.clientWidth ?? 0,
    dragOffsetPx: this.dragOffsetPx,
    isDragging: this.isDragging,
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startIndex'] && this.open()) {
      this.activeIndex.set(this.clampIndex(this.startIndex));
    }
  }

  ngOnDestroy(): void {
    this.unlockScroll();
    if (this.closeTimer) clearTimeout(this.closeTimer);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.open()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goPrev();
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goNext();
    }
  }

  show(index = 0): void {
    if (!this.items.length || !isPlatformBrowser(this.platformId)) return;
    this.closing.set(false);
    this.activeIndex.set(this.clampIndex(index));
    this.dragOffsetPx.set(0);
    this.isDragging.set(false);
    this.lockScroll();
    this.open.set(true);
  }

  close(): void {
    if (!this.open() || this.closing()) return;
    this.closing.set(true);
    this.closeTimer = setTimeout(() => {
      this.open.set(false);
      this.closing.set(false);
      this.unlockScroll();
    }, 220);
  }

  goPrev(): void {
    this.activeIndex.update((i) => Math.max(0, i - 1));
  }

  goNext(): void {
    this.activeIndex.update((i) => Math.min(this.items.length - 1, i + 1));
  }

  goTo(index: number): void {
    this.activeIndex.set(this.clampIndex(index));
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cg-photo-gallery')) {
      this.close();
    }
  }

  trackTransform(): string {
    return sliderTrackTransform(this.activeIndex(), this.dragOffsetPx());
  }

  shouldLoadImage(index: number): boolean {
    const active = this.activeIndex();
    return index === active || index === active - 1 || index === active + 1;
  }

  private clampIndex(index: number): number {
    if (!this.items.length) return 0;
    return Math.min(Math.max(0, index), this.items.length - 1);
  }

  private lockScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    document.body.style.overflow = this.previousBodyOverflow;
  }
}
